/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 批量编辑水印页面上下文 - 不使用reducer版本
 * @LastEditors: yingongjing
 * @LastEditTime: 2026-01-08 23:13:42
 */

import React, {
  createContext,
  useState,
  useCallback,
  useContext,
  ReactNode,
  useRef,
  useEffect,
  useMemo
} from 'react'
import { useDispatch } from 'react-redux'
import {
  updateAllPhotoPickerPhotosStatus,
  setPhotoPickerPhotos
} from '../../../../actions/photoPicker'
import { message } from 'antd'
import { useWatermarkExtraction } from '../hooks/useWatermarkExtraction'
import { usePhotoOperations } from '../hooks/usePhotoOperations'
import { usePendingPhotosProcessor } from '../hooks/usePendingPhotosProcessor'
import { useAppSelector } from '../../../../app/hooks'

// 定义水印提取状态枚举
export enum WatermarkExtractionStatus {
  EXTRACTING = 'extracting', // 提取中
  COMPLETED = 'completed', // 提取完成
  EXTRACTING_TIMEOUT = 'extracting_timeout', // 提取超时
  FAILED = 'failed', // 提取失败
  EDITING = 'editing', // 编辑中
  EDIT_COMPLETED = 'edit_completed', // 编辑完成
  EDIT_FAILED = 'edit_failed', // 编辑失败
  EDIT_NO_CHANGES = 'edit_no_changes', // 编辑无修改项（保持原状态，可继续编辑）
  EDIT_TIMEOUT = 'edit_timeout' // 编辑超时（保持原状态，可继续编辑）
}

// 定义照片数据类型
export interface PhotoItem {
  seq: number
  url: string
  aiURL: string
  smallURL: string
  watermarkStatus?: WatermarkExtractionStatus // 水印提取状态，默认为提取中
  watermarkData?: WatermarkItem[] // 水印数据，直接绑定到图片上
  watermarkID?: string // 水印ID
  errorMsg?: string // 错误信息（show_reason）
  reason?: string // 原始错误原因（用于测试环境调试）
  show_reason?: string // 显示错误原因
  taskID?: string // 任务ID（用于测试环境调试）
  isDownloaded?: boolean // 是否已下载
}

export interface WatermarkItem {
  watermarkID?: string
  id?: string
  item_id: string
  title: string
  content: string
  switch: boolean
  edit_type?: number // 编辑类型：0-禁用switch/title/content，1-禁用switch/title/content，2-支持switch/content，3-支持switch/title/content，4-禁用switch/title/content，5-支持switch/title/content，其他-禁用switch/title/content
  item_status: number // 条目的状态：0-初始，1-新增，2-编辑
  contentEdit?: {
    editType: number
    isMultiChoice?: boolean
    isMultiLevelInput?: boolean
    itemsList: string[]
    placeholder?: string
  }
  options?: {
    itemId: number
    records: {
      content: string
      childs: any[]
      uploadLocation: any[]
    }[]
  }[]
}

// 定义Context类型
export interface WatermarkBatchContextType {
  // 状态
  photoList: PhotoItem[]
  currentIndex: number
  currentPhoto: PhotoItem | null
  watermarkCnt: number
  hasUnsavedChanges: boolean // 是否有未保存的编辑修改
  isExtracting: boolean // 是否正在提取水印
  extractingPhotoCount: number // 正在提取的照片数量

  // 操作方法
  setPhotos: (photos: PhotoItem[]) => void
  setCurrentIndex: (index: number) => void
  updatePhotos: (newPhotos: PhotoItem[]) => void
  removePhoto: (photoIndex: number) => void
  removeFailedPhotos: () => void
  removePhotosBySeqs: (seqs: Array<number | string>) => void
  applyWatermarkToAll: (data: WatermarkItem[]) => Promise<void>
  updatePhotoWatermarkData: (
    itemIndex: number,
    watermarkData: WatermarkItem[]
  ) => void
  updatePhotosStatus: (
    photosToUpdate: PhotoItem[],
    status: WatermarkExtractionStatus
  ) => void
  setWatermarkCnt: (count: number) => void
  decrementWatermarkCnt: (count?: number) => void
  // 提取状态管理
  setExtracting: (isExtracting: boolean, photoCount?: number) => void
  // 检查照片是否可以选择/预览/编辑
  isPhotoSelectable: (photo: PhotoItem) => boolean
  // 滚动控制方法
  setShouldBlockScroll: (value: boolean) => void
  getShouldBlockScroll: () => boolean
  // 选中状态管理
  registerGetSelectedPhotos: (fn: () => PhotoItem[]) => void
  getSelectedPhotos: () => PhotoItem[]
  // 选中状态（用于与 ImageList 联动）
  selectedPhotoSeqs: Set<number>
  checkAll: boolean
  setSelectedPhotoSeqs: (
    seqs: Set<number> | ((prev: Set<number>) => Set<number>)
  ) => void
  setCheckAll: (checkAll: boolean) => void
  togglePhotoSelection: (photoSeq: number) => void
  // 编辑状态管理
  setHasUnsavedChanges: (hasChanges: boolean) => void
  // 问卷弹窗管理
  surveyModalVisible: boolean
  showSurveyModal: () => void
  hideSurveyModal: () => void
  // 上传弹窗管理（根 index 挂载，由子组件触发展示）
  uploadModalVisible: boolean
  setUploadModalVisible: (visible: boolean) => void
  // 下载进度弹窗（根 index 挂载，由子组件触发展示）
  downloadProgress: {
    visible: boolean
    current: number
    total: number
    title?: string
  }
  setDownloadProgress: (v: {
    visible: boolean
    current: number
    total: number
    title?: string
  }) => void
  // 全屏预览（根 index 挂载，由子组件触发展示）
  fullscreenPreview: {
    visible: boolean
    imageList: string[]
    index: number
    photoType: 'success' | 'failed' | null
    failedPhotos: PhotoItem[]
  }
  openFullscreenPreview: (params: {
    imageList: string[]
    index: number
    photoType?: 'success' | 'failed'
    failedPhotos?: PhotoItem[]
  }) => void
  closeFullscreenPreview: () => void
  // 上传确认回调（由 ImagePreview 注册 handleUploadEditedPhotos，根 index 在上传弹窗 onConfirm 时调用，folderName 用于成功提示及跳转）
  registerUploadConfirmHandler: (
    fn:
      | ((
          selectedPhotos: PhotoItem[],
          folderID: number,
          folderName?: string,
          options?: { suppressToast?: boolean }
        ) => Promise<PhotoItem[]>)
      | null
  ) => void
  invokeUploadConfirm: (
    selectedPhotos: PhotoItem[],
    folderID: number,
    folderName?: string,
    options?: { suppressToast?: boolean }
  ) => Promise<PhotoItem[]>
  // 下载全部回调（由 ImagePreview 注册 handleDownloadAll，根/确认离开弹窗等可触发）
  registerDownloadAllHandler: (fn: (() => void | Promise<void>) | null) => void
  invokeDownloadAll: () => void | Promise<void>
}

// Context创建
const WatermarkBatchContext = createContext<
  WatermarkBatchContextType | undefined
>(undefined)

// Provider组件Props
interface WatermarkBatchProviderProps {
  children: ReactNode
  initialPhotos?: PhotoItem[]
}

// Provider组件
export const WatermarkBatchProvider: React.FC<WatermarkBatchProviderProps> = ({
  children
}) => {
  // 使用 dispatch 函数
  const dispatch = useDispatch()

  // 从 Redux store 获取 watermarkCnt（剩余编辑次数）
  // 使用 getIn 方法获取嵌套的 watermarkCnt（类似于 useVip hook 的做法）
  const watermarkCntFromStore = useAppSelector((state) => {
    return state.group.vipInfo?.watermarkCnt || 0
  })

  // 状态定义
  const [photoList, setPhotoList] = useState<PhotoItem[]>([])
  const [currentIndex, setCurrentIndex] = useState<number>(-1)
  const [watermarkCnt, setWatermarkCntState] = useState<number>(0)
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState<boolean>(
    false
  )
  const [isExtracting, setIsExtracting] = useState<boolean>(false)
  const [extractingPhotoCount, setExtractingPhotoCount] = useState<number>(0)
  // 问卷弹窗状态
  const [surveyModalVisible, setSurveyModalVisible] = useState<boolean>(false)
  // 上传弹窗状态（根 index 挂载）
  const [uploadModalVisible, setUploadModalVisible] = useState<boolean>(false)
  // 下载进度弹窗状态（根 index 挂载）
  const [downloadProgress, setDownloadProgressState] = useState<{
    visible: boolean
    current: number
    total: number
    title?: string
  }>({ visible: false, current: 0, total: 0 })
  // 全屏预览状态（根 index 挂载）
  const [fullscreenPreview, setFullscreenPreviewState] = useState<{
    visible: boolean
    imageList: string[]
    index: number
    photoType: 'success' | 'failed' | null
    failedPhotos: PhotoItem[]
  }>({
    visible: false,
    imageList: [],
    index: 0,
    photoType: null,
    failedPhotos: []
  })
  // 上传确认回调（由 ImagePreview 注册）
  const uploadConfirmHandlerRef = useRef<
    | ((
        selectedPhotos: PhotoItem[],
        folderID: number,
        folderName?: string,
        options?: { suppressToast?: boolean }
      ) => Promise<PhotoItem[]>)
    | null
  >(null)
  // 下载全部回调（由 ImagePreview 注册）
  const downloadAllHandlerRef = useRef<(() => void | Promise<void>) | null>(
    null
  )

  // 当 Redux store 中的 watermarkCnt 变化时，更新 watermarkCnt
  useEffect(() => {
    if (watermarkCntFromStore !== undefined && watermarkCntFromStore !== null) {
      setWatermarkCntState(watermarkCntFromStore)
    }
  }, [watermarkCntFromStore])
  // 使用 ref 保存最新的 photos 状态，以便在回调中访问
  const photosRef = useRef<PhotoItem[]>(photoList)

  // 更新 ref 值
  useEffect(() => {
    photosRef.current = photoList
  }, [photoList])

  // 保存当前选中照片 seq，避免新增照片时索引偏移导致选中被替换
  const currentSelectedSeqRef = useRef<number | null>(null)
  useEffect(() => {
    if (currentIndex >= 0 && currentIndex < photoList.length) {
      currentSelectedSeqRef.current = photoList[currentIndex]?.seq ?? null
    } else {
      currentSelectedSeqRef.current = null
    }
  }, [currentIndex, photoList])

  // 保存当前选中索引，供异步回调判断
  const currentIndexRef = useRef<number>(currentIndex)
  useEffect(() => {
    currentIndexRef.current = currentIndex
  }, [currentIndex])

  // 标记是否禁止滚动（用户手动点击选中时禁止，提取完成和编辑完成时允许）
  // 使用 ref 而不是 state，避免触发不必要的重新渲染
  const shouldBlockScrollRef = useRef<boolean>(false)

  // 设置禁止滚动标记
  const setShouldBlockScroll = useCallback((value: boolean) => {
    shouldBlockScrollRef.current = value
  }, [])

  // 获取禁止滚动标记
  const getShouldBlockScroll = useCallback(() => {
    return shouldBlockScrollRef.current
  }, [])

  // 选中状态管理（用于与 ImageList 和 BatchApplyDrawer 联动）
  const [selectedPhotoSeqs, setSelectedPhotoSeqsState] = useState<Set<number>>(
    new Set()
  )
  const [checkAll, setCheckAllState] = useState<boolean>(false)

  // 设置选中照片 seq 集合
  const setSelectedPhotoSeqs = useCallback(
    (seqs: Set<number> | ((prev: Set<number>) => Set<number>)) => {
      if (typeof seqs === 'function') {
        setSelectedPhotoSeqsState(seqs)
      } else {
        setSelectedPhotoSeqsState(seqs)
      }
    },
    []
  )

  // 设置全选状态
  const setCheckAll = useCallback((checkAll: boolean) => {
    setCheckAllState(checkAll)
  }, [])

  // 切换单个照片的选中状态
  const togglePhotoSelection = useCallback((photoSeq: number) => {
    setSelectedPhotoSeqsState((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(photoSeq)) {
        newSet.delete(photoSeq)
      } else {
        newSet.add(photoSeq)
      }
      return newSet
    })
  }, [])

  // 存储获取选中照片的函数（由 ImageList 组件注册）
  const getSelectedPhotosRef = useRef<(() => PhotoItem[]) | null>(null)

  // 注册获取选中照片的函数
  const registerGetSelectedPhotos = useCallback((fn: () => PhotoItem[]) => {
    getSelectedPhotosRef.current = fn
  }, [])

  // 获取选中的照片
  const getSelectedPhotos = useCallback((): PhotoItem[] => {
    if (getSelectedPhotosRef.current) {
      return getSelectedPhotosRef.current()
    }
    return []
  }, [])

  // 使用 useMemo 优化 currentPhoto，只有当 currentIndex 或对应照片的实际内容变化时才更新
  // 使用 ref 保存上一次的照片，通过比较关键字段来判断是否需要更新
  const prevCurrentPhotoRef = useRef<PhotoItem | null>(null)
  const prevCurrentIndexRef = useRef<number>(-1)

  const currentPhoto = useMemo(() => {
    if (currentIndex >= 0 && currentIndex < photoList.length) {
      const photo = photoList[currentIndex]
      if (!photo) return null

      // 如果索引没变，且照片的关键字段没变，返回上一次的对象引用
      if (
        prevCurrentIndexRef.current === currentIndex &&
        prevCurrentPhotoRef.current &&
        prevCurrentPhotoRef.current.smallURL === photo.smallURL &&
        prevCurrentPhotoRef.current.watermarkStatus === photo.watermarkStatus &&
        prevCurrentPhotoRef.current.aiURL === photo.aiURL &&
        prevCurrentPhotoRef.current.errorMsg === photo.errorMsg &&
        prevCurrentPhotoRef.current.taskID === photo.taskID &&
        prevCurrentPhotoRef.current.watermarkData === photo.watermarkData &&
        prevCurrentPhotoRef.current.watermarkID === photo.watermarkID &&
        prevCurrentPhotoRef.current.reason === photo.reason &&
        prevCurrentPhotoRef.current.show_reason === photo.show_reason
      ) {
        return prevCurrentPhotoRef.current
      }

      // 否则创建新对象并保存引用
      prevCurrentIndexRef.current = currentIndex
      prevCurrentPhotoRef.current = photo
      return photo
    }
    prevCurrentIndexRef.current = -1
    prevCurrentPhotoRef.current = null
    return null
  }, [currentIndex, photoList])

  // 设置照片
  const setPhotos = useCallback((newPhotos: PhotoItem[]) => {
    const normalizedPhotos = newPhotos.map((photo) => {
      return {
        ...photo,
        // 确保有默认状态
        watermarkStatus:
          photo.watermarkStatus || WatermarkExtractionStatus.EXTRACTING
      }
    })

    setPhotoList([...normalizedPhotos])
  }, [])

  // 检查照片是否可以选择/预览/编辑
  const isPhotoSelectable = useCallback((photo: PhotoItem) => {
    const isFailed = photo.watermarkStatus === WatermarkExtractionStatus.FAILED
    const isExtractingTimeout =
      photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING_TIMEOUT
    const isExtracting =
      photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING

    // 如果照片是失败状态、提取超时或正在提取中，不允许选择
    return !isFailed && !isExtractingTimeout && !isExtracting
  }, [])

  // 批量更新照片状态（同时更新本地状态和PhotoPicker状态）
  const updatePhotosStatus = useCallback(
    (photosToUpdate: PhotoItem[], status: WatermarkExtractionStatus) => {
      setPhotoList((prevPhotos) => {
        // 创建 smallURL 到索引的映射，提高查找效率（O(1) vs O(n)）
        const urlToIndexMap = new Map<string, number>()
        prevPhotos.forEach((photo, index) => {
          urlToIndexMap.set(photo.smallURL, index)
        })

        const updatedPhotos = [...prevPhotos]
        photosToUpdate.forEach((photoToUpdate) => {
          // 使用 Map 快速查找索引，避免 O(n) 的 findIndex 操作
          const photoIndex = urlToIndexMap.get(photoToUpdate.smallURL)
          if (photoIndex !== undefined && photoIndex !== -1) {
            const oldStatus = updatedPhotos[photoIndex].watermarkStatus

            // 如果原照片是提取失败状态，且新状态是编辑完成，则不更新状态
            // 这样可以防止将提取失败的照片误标记为编辑完成
            if (
              oldStatus === WatermarkExtractionStatus.FAILED &&
              status === WatermarkExtractionStatus.EDIT_COMPLETED
            ) {
              // 不更新状态，保持提取失败状态
              return
            }

            // 对于其他所有状态转换，都允许更新（包括 EDIT_COMPLETED -> EDIT_FAILED）
            // 创建新的照片对象，确保所有字段都被正确更新
            const updateFields: Partial<PhotoItem> = {
              watermarkStatus: status
            }

            // 如果传入的照片包含 aiURL，则更新它（即使为空字符串也要更新）
            if (photoToUpdate.aiURL !== undefined) {
              updateFields.aiURL = photoToUpdate.aiURL
            }

            // 如果传入的照片包含错误信息，则更新错误信息
            if (photoToUpdate.errorMsg !== undefined) {
              updateFields.errorMsg = photoToUpdate.errorMsg
            }

            // 如果传入的照片包含 reason，则更新它
            if (photoToUpdate.reason !== undefined) {
              updateFields.reason = photoToUpdate.reason
            }

            // 如果传入的照片包含水印数据，则更新它
            if (photoToUpdate.watermarkData !== undefined) {
              updateFields.watermarkData = photoToUpdate.watermarkData
            }

            // 如果传入的照片包含 watermarkID，则更新它
            if (photoToUpdate.watermarkID !== undefined) {
              updateFields.watermarkID = photoToUpdate.watermarkID
            }

            // 如果传入的照片包含 taskID，则更新它
            if (photoToUpdate.taskID !== undefined) {
              updateFields.taskID = photoToUpdate.taskID
            }

            // 如果传入的照片包含 isDownloaded，则更新它
            if (photoToUpdate.isDownloaded !== undefined) {
              updateFields.isDownloaded = photoToUpdate.isDownloaded
            }

            // 合并更新字段，创建新的照片对象
            // 确保即使状态相同，也会创建新对象以触发重新渲染
            updatedPhotos[photoIndex] = {
              ...updatedPhotos[photoIndex],
              ...updateFields
            }
          }
        })
        return updatedPhotos
      })

      // 将照片状态更新到PhotoPicker
      // 注意：PhotoPicker reducer使用seq匹配照片，所以我们需要传入完整的照片对象
      // photosToUpdate数组中的每个photo都包含seq字段，PhotoPicker会使用这个seq来匹配和更新照片状态
      dispatch(updateAllPhotoPickerPhotosStatus(photosToUpdate, status))
    },
    [dispatch]
  )

  // 设置提取状态（需要在 usePendingPhotosProcessor 之前定义）
  const setExtracting = useCallback(
    (extracting: boolean, photoCount: number = 0) => {
      setIsExtracting(extracting)
      setExtractingPhotoCount(photoCount)
    },
    []
  )

  // 使用照片操作 Hook（需要在 updatePhotos 之前定义，以便使用 normalizePhotos）
  const {
    applyWatermarkToAll,
    normalizePhotos,
    removePhoto,
    removeFailedPhotos
  } = usePhotoOperations(photoList, setPhotoList, currentIndex, setCurrentIndex)

  const removePhotosBySeqs = useCallback(
    (seqs: Array<number | string>) => {
      if (!seqs || seqs.length === 0) {
        return
      }
      console.warn('removePhotosBySeqs: start', {
        seqs,
        photoListLength: photosRef.current.length
      })
      const seqSet = new Set(seqs.map((seq) => String(seq)))
      const originPhotos = photosRef.current
      const remainingPhotos = originPhotos.filter(
        (photo) => !seqSet.has(String(photo.seq))
      )
      const removedCount = originPhotos.length - remainingPhotos.length
      console.warn('removePhotosBySeqs: result', {
        removedCount,
        remainingLength: remainingPhotos.length,
        removedSeqs: originPhotos
          .filter((photo) => seqSet.has(String(photo.seq)))
          .map((photo) => photo.seq),
        sampleRemainingSeqs: remainingPhotos
          .slice(0, 10)
          .map((photo) => photo.seq)
      })

      setPhotoList([...remainingPhotos])
      dispatch(setPhotoPickerPhotos(remainingPhotos, 'batch-editor'))
      setCheckAllState(false)
      setSelectedPhotoSeqsState((prev) => {
        const next = new Set(prev)
        seqs.forEach((seq) => {
          const numSeq = Number(seq)
          if (!Number.isNaN(numSeq)) {
            next.delete(numSeq)
          }
          next.delete(seq as number)
        })
        return next
      })

      if (remainingPhotos.length === 0) {
        setCurrentIndex(-1)
        return
      }

      const currentPhotoSeq = originPhotos[currentIndexRef.current]?.seq
      if (currentPhotoSeq && seqSet.has(String(currentPhotoSeq))) {
        setCurrentIndex(0)
        return
      }

      const newIndex = remainingPhotos.findIndex(
        (photo) => photo.seq === currentPhotoSeq
      )
      if (newIndex !== -1) {
        setCurrentIndex(newIndex)
      } else if (currentIndexRef.current >= remainingPhotos.length) {
        setCurrentIndex(remainingPhotos.length - 1)
      }
    },
    [dispatch]
  )

  // 使用待提取照片处理 hook
  const { processPendingPhotos } = usePendingPhotosProcessor(
    photosRef,
    setPhotoList,
    updatePhotosStatus,
    setCurrentIndex,
    setExtracting,
    () => currentIndexRef.current === -1
  )

  // 更新照片列表（在现有照片前添加新照片）
  const updatePhotos = useCallback(
    (newPhotos: PhotoItem[]) => {
      const selectedSeq = currentSelectedSeqRef.current

      setPhotoList((prevPhotos) => {
        // 创建现有照片的 smallURL Set，用于快速查找
        const existingPhotoUrls = new Set(
          prevPhotos.map((photo) => photo.smallURL)
        )

        // 筛选出真正的新照片（不在现有列表中的照片）
        const trulyNewPhotos = newPhotos.filter(
          (photo) => !existingPhotoUrls.has(photo.smallURL)
        )

        // 只对新照片进行标准化处理，设置默认状态为 EXTRACTING
        const normalizedNewPhotos = normalizePhotos(trulyNewPhotos)

        // 合并新照片到现有列表前面，保持已有照片的状态不变
        const mergedPhotos = [...normalizedNewPhotos, ...prevPhotos]

        // 新增照片会插入到列表前面，保持原选中照片不变
        if (selectedSeq !== null) {
          const newIndex = mergedPhotos.findIndex(
            (photo) => photo.seq === selectedSeq
          )
          if (newIndex !== -1) {
            setCurrentIndex(newIndex)
          }
        }

        // 筛选出需要提取水印的照片（只包括新添加的照片）
        const pendingPhotos = normalizedNewPhotos.filter(
          (photo) =>
            photo.watermarkStatus === WatermarkExtractionStatus.EXTRACTING
        )

        // 使用hook处理待提取照片（只处理新照片）
        // 使用 setTimeout 确保状态更新完成后再处理
        if (pendingPhotos.length > 0) {
          setTimeout(() => {
            processPendingPhotos(pendingPhotos)
          }, 0)
        }

        return mergedPhotos
      })
    },
    [normalizePhotos, processPendingPhotos, setCurrentIndex]
  )

  // 更新照片水印数据
  const updatePhotoWatermarkData = useCallback(
    (photoIndex: number, watermarkData: WatermarkItem[]) => {
      setPhotoList((prevPhotos) => {
        const updatedPhotos = [...prevPhotos]
        updatedPhotos[photoIndex] = {
          ...updatedPhotos[photoIndex],
          watermarkData: watermarkData
        }
        return updatedPhotos
      })
    },
    []
  )

  // 设置剩余编辑次数
  const setWatermarkCnt = useCallback((count: number) => {
    setWatermarkCntState(count)
  }, [])

  // 减少编辑次数
  const decrementWatermarkCnt = useCallback((count: number = 1) => {
    setWatermarkCntState((prevCount) => Math.max(0, prevCount - count))
  }, [])

  // 编辑状态管理
  const setHasUnsavedChanges = useCallback((hasChanges: boolean) => {
    setHasUnsavedChangesState(hasChanges)
  }, [])

  // 问卷弹窗管理
  // 检查是否应该显示问卷弹窗（首次下载完成且未关闭过）
  const shouldShowSurveyModal = useCallback(() => {
    const surveyClosed = localStorage.getItem(
      'watermarkBatchEditor_surveyClosed'
    )
    return !surveyClosed
  }, [])

  // 显示问卷弹窗
  const showSurveyModal = useCallback(() => {
    if (shouldShowSurveyModal()) {
      setSurveyModalVisible(true)
    }
  }, [shouldShowSurveyModal])

  // 隐藏问卷弹窗（并记录已关闭状态）
  const hideSurveyModal = useCallback(() => {
    localStorage.setItem('watermarkBatchEditor_surveyClosed', 'true')
    setSurveyModalVisible(false)
  }, [])

  // 下载进度 setter（根 index 挂载的弹窗由子组件通过此更新）
  const setDownloadProgress = useCallback(
    (v: {
      visible: boolean
      current: number
      total: number
      title?: string
    }) => {
      setDownloadProgressState(v)
    },
    []
  )

  // 全屏预览：打开
  const openFullscreenPreview = useCallback(
    (params: {
      imageList: string[]
      index: number
      photoType?: 'success' | 'failed'
      failedPhotos?: PhotoItem[]
    }) => {
      setFullscreenPreviewState({
        visible: true,
        imageList: params.imageList,
        index: params.index,
        photoType: params.photoType ?? null,
        failedPhotos: params.failedPhotos ?? []
      })
    },
    []
  )

  // 全屏预览：关闭
  const closeFullscreenPreview = useCallback(() => {
    setFullscreenPreviewState((prev) => ({
      ...prev,
      visible: false,
      imageList: [],
      index: 0,
      photoType: null,
      failedPhotos: []
    }))
  }, [])

  // 注册上传确认回调（ImagePreview 挂载时注册 handleUploadEditedPhotos）
  const registerUploadConfirmHandler = useCallback(
    (
      fn:
        | ((
            selectedPhotos: PhotoItem[],
            folderID: number,
            folderName?: string,
            options?: { suppressToast?: boolean }
          ) => Promise<PhotoItem[]>)
        | null
    ) => {
      uploadConfirmHandlerRef.current = fn
    },
    []
  )

  // 调用已注册的上传确认回调（根 index 在上传弹窗 onConfirm 时调用）
  const invokeUploadConfirm = useCallback(
    (
      selectedPhotos: PhotoItem[],
      folderID: number,
      folderName?: string,
      options?: { suppressToast?: boolean }
    ) => {
      if (uploadConfirmHandlerRef.current) {
        return uploadConfirmHandlerRef.current(
          selectedPhotos,
          folderID,
          folderName,
          options
        )
      }
      return Promise.resolve([])
    },
    []
  )

  const registerDownloadAllHandler = useCallback(
    (fn: (() => void | Promise<void>) | null) => {
      downloadAllHandlerRef.current = fn
    },
    []
  )

  const invokeDownloadAll = useCallback(() => {
    if (downloadAllHandlerRef.current) {
      return downloadAllHandlerRef.current()
    }
  }, [])

  // Context值
  const contextValue: WatermarkBatchContextType = {
    // 状态
    photoList,
    currentIndex,
    currentPhoto,
    watermarkCnt,
    hasUnsavedChanges,
    isExtracting,
    extractingPhotoCount,
    // 操作方法
    setPhotos,
    setCurrentIndex,
    updatePhotos,
    removePhoto,
    removeFailedPhotos,
    removePhotosBySeqs,
    applyWatermarkToAll,
    updatePhotoWatermarkData,
    updatePhotosStatus,
    setWatermarkCnt,
    decrementWatermarkCnt,
    setExtracting,
    isPhotoSelectable,
    // 滚动控制方法
    setShouldBlockScroll,
    getShouldBlockScroll,
    // 选中状态管理
    registerGetSelectedPhotos,
    getSelectedPhotos,
    // 选中状态（用于与 ImageList 联动）
    selectedPhotoSeqs,
    checkAll,
    setSelectedPhotoSeqs,
    setCheckAll,
    togglePhotoSelection,
    // 编辑状态管理
    setHasUnsavedChanges,
    // 问卷弹窗管理
    surveyModalVisible,
    showSurveyModal,
    hideSurveyModal,
    // 上传弹窗管理（根 index 挂载）
    uploadModalVisible,
    setUploadModalVisible,
    // 下载进度弹窗（根 index 挂载）
    downloadProgress,
    setDownloadProgress,
    // 全屏预览（根 index 挂载）
    fullscreenPreview,
    openFullscreenPreview,
    closeFullscreenPreview,
    // 上传确认回调
    registerUploadConfirmHandler,
    invokeUploadConfirm,
    registerDownloadAllHandler,
    invokeDownloadAll
  }

  return (
    <WatermarkBatchContext.Provider value={contextValue}>
      {children}
    </WatermarkBatchContext.Provider>
  )
}

// 为了保持向后兼容，导出WatermarkBatchContextProvider别名
export const WatermarkBatchContextProvider = WatermarkBatchProvider

export const useWatermarkBatchContext = (): WatermarkBatchContextType => {
  const context = useContext(WatermarkBatchContext)

  if (!context) {
    throw new Error(
      'useWatermarkBatch must be used within a WatermarkBatchProvider'
    )
  }

  return context
}

export default WatermarkBatchContext
